// src/components/layout/Sidebar.jsx
import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { ROUTES } from "../../constants";
import { Loading } from "../common/Loading";
import "../../assets/css/sidebar.css";
import {
  FaMapMarkedAlt,
  FaClipboardList,
  FaTachometerAlt,
  FaUser,
  FaTools,
  FaUsers,
  FaSignOutAlt,
  FaBars
} from "react-icons/fa";
import { formatText } from "../../components/common/formatHelpers";


const NavItem = ({ to, icon, children, isActive }) => {
  return (
    <Link
      to={to}
      className={`nav-item ${isActive ? "active" : ""}`}
    >
      <span className="nav-item-icon">{icon}</span>
      <span>{children}</span>
    </Link>
  );
};

export default function Sidebar() {
  const { isAuthenticated, loading, userData, hasAdminAccess, logout, isCiudadano, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Si hay una ruta solicitada desde logout, usarla; si no, ir a LOGIN.
      const postLogout = (() => {
        try {
          return sessionStorage.getItem("postLogoutRedirect");
        } catch (e) {
          return null;
        }
      })();

      const target = postLogout || ROUTES.LOGIN;

      // Borrar la flag para no reutilizarla luego
      try {
        sessionStorage.removeItem("postLogoutRedirect");
      } catch (e) {
        // ignore
      }

      navigate(target);
    }
  }, [isAuthenticated, loading, navigate]);

  // Close sidebar when route changes (useful for mobile)
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const isActiveRoute = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout(ROUTES.WELCOME);
    } catch (err) {
      console.error("Error al cerrar sesión:", err);
    }
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: "var(--bg-body)"
      }}>
        <Loading text="Cargando aplicación..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="app-layout">
      {/* Mobile Toggle & Overlay */}
      <button className="mobile-toggle" onClick={toggleMobileSidebar} aria-label="Menu">
        <FaBars />
      </button>

      <div
        className={`sidebar-overlay ${isMobileOpen ? "visible" : ""}`}
        onClick={() => setIsMobileOpen(false)}
      />

      {/* SIDEBAR */}
      <div className={`sidebar ${isMobileOpen ? "open" : ""}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <img src={""} alt="logo municipal" />
            <h1>Gestión de Reclamos</h1>
          </div>

          <div className="sidebar-nav">
            <div className="sidebar-section-title">Principal</div>
            <NavItem to={ROUTES.HOME} icon={<FaMapMarkedAlt />} isActive={isActiveRoute(ROUTES.HOME)}>MAPA</NavItem>
            {isCiudadano && (
              <>
                <NavItem to={ROUTES.MIS_RECLAMOS} icon={<FaClipboardList />} isActive={isActiveRoute(ROUTES.MIS_RECLAMOS)}>MIS RECLAMOS</NavItem>
              </>
            )}
            <NavItem to={ROUTES.PERFIL} icon={<FaUser />} isActive={isActiveRoute(ROUTES.PERFIL)}>PERFIL</NavItem>

            {hasAdminAccess && (
              <>
                <div className="sidebar-section-title">Administración</div>
                <NavItem to={ROUTES.DASHBOARD} icon={<FaTachometerAlt />} isActive={isActiveRoute(ROUTES.DASHBOARD)}>DASHBOARD</NavItem>
                <NavItem to={ROUTES.GESTIONAR_RECLAMOS} icon={<FaTools />} isActive={isActiveRoute(ROUTES.GESTIONAR_RECLAMOS)}>GESTIONAR RECLAMOS</NavItem>
                {isAdmin && (
                  <NavItem to={ROUTES.GESTIONAR_USUARIOS} icon={<FaUsers />} isActive={isActiveRoute(ROUTES.GESTIONAR_USUARIOS)}>GESTIONAR USUARIOS</NavItem>
                )}
              </>
            )}
          </div>

          <div className="sidebar-user">
            <div className="email">{userData?.email}</div>
            <div className="role">{formatText(userData?.role) || "usuario"}</div>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            <FaSignOutAlt /> Cerrar Sesión
          </button>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="main-content">
        <div className="main-content-inner">
          <Outlet />
        </div>
      </div>
    </div>
  );
}